import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const userData = response.data.user;
      
      // Check if user is admin
      if (userData.role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }

      login(response.data.token, userData);
      toast.success('Welcome to Admin Panel!');
      
      setTimeout(() => {
        navigate('/admin');
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-foreground flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border-4 border-secondary rounded-sm shadow-hard p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-secondary rounded-sm flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-3xl font-heading font-bold text-foreground">Admin Access</h1>
            <p className="text-muted-foreground font-medium mt-2">Excel Era Auto Care</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="font-bold uppercase text-sm tracking-wide">Admin Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="admin-login-email-input"
                className="input-field mt-2"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="password" className="font-bold uppercase text-sm tracking-wide">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="admin-login-password-input"
                className="input-field mt-2"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-btn"
              className="w-full h-12 text-lg bg-secondary hover:bg-secondary/90 text-white font-bold uppercase tracking-wide rounded-sm"
            >
              {loading ? 'Verifying...' : 'Admin Login'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground font-medium" data-testid="admin-back-link">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
